import React, { useState, useEffect, useRef } from 'react';
import moment from 'moment';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import EmojiPicker from 'emoji-picker-react';
// Import the new RecurrenceField component
import RecurrenceField from './RecurrenceField';
// Import our new WebBudgetCategoryPicker component
import BudgetCategoryPicker from './BudgetCategoryPicker';
// Import the useFinanceData hook to access budgets
import { useFinanceData } from '../hooks/useFinanceData';

// Simplified emoji picker for web
const ImprovedEmojiPicker = ({ value, onChange }) => {
  const [showPicker, setShowPicker] = useState(false);

  return (
    <div className="emoji-picker-container">
      <button 
        type="button" 
        className="emoji-button" 
        onClick={() => setShowPicker(!showPicker)}
      >
        {value || '💸'}
      </button>
      {showPicker && (
        <div className="emoji-picker-wrapper">
          <EmojiPicker
            onEmojiClick={(emojiObject) => {
              onChange(emojiObject.emoji);
              setShowPicker(false);
            }}
          />
        </div>
      )}
    </div>
  );
};

// Notification preference component
const NotificationPreference = ({ value, onChange }) => {
  const options = [
    { value: 'off', label: 'Off' },
    { value: '0', label: 'Same day' },
    { value: '1', label: '1 day before' },
    { value: '2', label: '2 days before' },
    { value: '7', label: '1 week before' },
  ];

  const handleChange = (event) => {
    const { value: optionValue, checked } = event.target;
    let newValues;
    
    if (optionValue === 'off') {
      // If "Off" is selected, clear all other selections
      newValues = checked ? ['off'] : [];
    } else {
      // If any other option is selected, remove "Off" and add/remove the selected option
      newValues = value.filter(v => v !== 'off');
      
      if (checked) {
        newValues.push(optionValue);
      } else {
        newValues = newValues.filter(v => v !== optionValue);
      }
      
      // If no options are selected, default to "Off"
      if (newValues.length === 0) {
        newValues = ['off'];
      }
    }
    
    onChange(newValues);
  };

  return (
    <div className="notification-preferences">
      {options.map(option => (
        <div key={option.value} className="form-check">
          <input
            type="checkbox"
            id={`notification-${option.value}`}
            className="form-check-input"
            value={option.value}
            checked={value.includes(option.value)}
            onChange={handleChange}
          />
          <label 
            className="form-check-label" 
            htmlFor={`notification-${option.value}`}
          >
            {option.label}
          </label>
        </div>
      ))}
    </div>
  );
};

const ExpenseForm = ({ onSubmit, onCancel, onDelete, initialValues, budgetCategoryId, isOnboarding }) => {
  // Use the useFinanceData hook to access budgets
  const { getBudgetCategories } = useFinanceData();
  
  const [expense, setExpense] = useState({
    title: '',
    amount: 0,
    date: moment(),
    recurrenceFrequency: 1,
    recurrencePeriod: 'none',
    emoji: '💸',
    budgetCategoryId: null,
    notificationPreference: ['off'],
    reminder: {
      enabled: false,
      days: [],
      time: '08:00',
    },
  });

  const titleInputRef = useRef(null);

  // Get all budgets from our hook instead of the placeholder function
  const allBudgets = getBudgetCategories();

  useEffect(() => {
    if (initialValues) {
      const updatedExpense = {
        ...initialValues,
        date: moment(initialValues.date),
        amount: initialValues.amount ? Math.abs(initialValues.amount) : 0,
        recurrenceFrequency: initialValues.recurrenceFrequency || 1,
        notificationPreference: initialValues.notificationPreference || ['off'],
        reminder: initialValues.reminder || { enabled: false, days: [], time: '08:00' },
        budgetCategoryId: budgetCategoryId || initialValues.budgetCategoryId || null,
      };

      setExpense(updatedExpense);
    }

    // Focus the title input when the form loads
    if (titleInputRef.current) {
      titleInputRef.current.focus();
    }
  }, [initialValues, budgetCategoryId]);

  const handleChange = (name, value) => {
    setExpense({ ...expense, [name]: value });
  };

  const handleFrequencyChange = value => {
    // Handle new RecurrenceField component's frequency value format
    const frequency = value === 'none' ? 1 : parseInt(value, 10);
    handleChange('recurrenceFrequency', frequency);
  };

  const handleAmountChange = e => {
    const wholeNumber = parseInt(e.target.value, 10) || 0;
    handleChange('amount', wholeNumber);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!isOnboarding && !expense.budgetCategoryId) {
      alert('Please select a budget category for this expense.');
      return;
    }

    if (moment(expense.date).isBefore(moment(), 'day')) {
      alert('Please select a date that is not in the past.');
      return;
    }

    if (!expense.title || expense.amount <= 0) {
      alert('Please enter a title and a valid amount.');
      return;
    }

    const expenseData = {
      title: expense.title,
      amount: expense.amount, // We'll let the addItem function convert to cents
      type: 'expense',
      date: expense.date.format('YYYY-MM-DDTHH:mm:ss.SSSZ'),
      budgetCategoryId: isOnboarding ? budgetCategoryId : expense.budgetCategoryId,
      emoji: expense.emoji,
      notificationPreference: expense.notificationPreference,
    };

    if (expense.recurrencePeriod !== 'none') {
      expenseData.recurrenceFrequency = expense.recurrenceFrequency;
      expenseData.recurrencePeriod = expense.recurrencePeriod;
    }

    if (window.confirm(`Are you sure you want to add this expense?\n\nTitle: ${expenseData.title}\nAmount: $${expenseData.amount}`)) {
      onSubmit(expenseData);
    }
  };

  const handleDelete = () => {
    if (window.confirm('Are you sure you want to delete this expense?')) {
      onDelete(expense);
    }
  };

  // Get placeholder text based on budget category
  const getTitlePlaceholder = () => {
    if (!expense.budgetCategoryId) return "e.g. Some expense";
    
    const budget = allBudgets.find(b => b.id === expense.budgetCategoryId);
    if (!budget) return "e.g. Some expense";
    
    // Category-specific placeholders
    const placeholders = {
      'housing': "e.g., Rent, Mortgage, Property Tax",
      'utilities': "e.g., Electricity, Water, Internet",
      'food': "e.g., Groceries, Takeout, Coffee Shop",
      'transportation': "e.g., Gas, Car Payment, Public Transit",
      'entertainment': "e.g., Movies, Concerts, Streaming",
      'personal': "e.g., Haircut, Gym, Subscription",
    };
    
    const normalizedCategory = budget.title.toLowerCase().replace(/\s+/g, '');
    
    for (const [key, value] of Object.entries(placeholders)) {
      if (normalizedCategory.includes(key)) {
        return value;
      }
    }
    
    return `e.g., ${budget.title} expense`;
  };

  return (
    <div className="modal-backdrop">
      <div className="form-container">
        <h2 className="form-title">ADD NEW EXPENSE</h2>
        <form onSubmit={handleSubmit}>
          {!isOnboarding && (
            <div className="form-group">
              <label className="form-label">Budget Category</label>
              <BudgetCategoryPicker
                selectedCategoryId={expense.budgetCategoryId}
                onCategoryChange={value => handleChange('budgetCategoryId', value)}
                budgetItems={allBudgets}
                disabled={false}
              />
            </div>
          )}

          <div className="form-group">
            <label className="form-label">Title</label>
            <input
              ref={titleInputRef}
              type="text"
              className="form-control"
              placeholder={getTitlePlaceholder()}
              value={expense.title}
              onChange={e => handleChange('title', e.target.value)}
            />
          </div>

          <div className="form-group">
            <label className="form-label">Icon</label>
            <ImprovedEmojiPicker
              value={expense.emoji}
              onChange={value => handleChange('emoji', value)}
            />
          </div>

          <div className="form-group">
            <label className="form-label">Amount</label>
            <div className="currency-input-group">
              <span className="currency-symbol">$</span>
              <input
                type="number"
                className="form-control"
                value={expense.amount}
                onChange={handleAmountChange}
                min="0"
              />
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Due Date</label>
            <DatePicker
              selected={expense.date.toDate()}
              onChange={date => handleChange('date', moment(date))}
              className="form-control"
              minDate={new Date()}
              dateFormat="MMM d, yyyy"
            />
          </div>

          <div className="form-group">
            {/* Replace the old RecurrenceField with the imported one */}
            <RecurrenceField
              frequency={expense.recurrenceFrequency}
              period={expense.recurrencePeriod}
              onFrequencyChange={handleFrequencyChange}
              onPeriodChange={value => handleChange('recurrencePeriod', value)}
            />
          </div>

          <div className="form-group">
            <label className="form-label">Notifications</label>
            <NotificationPreference
              value={expense.notificationPreference}
              onChange={value => {
                setExpense(prev => ({
                  ...prev,
                  notificationPreference: value,
                  reminder: {
                    ...prev.reminder,
                    enabled: !value.includes('off'),
                    days: value.includes('off') ? [] : value.map(v => parseInt(v, 10)),
                  },
                }));
              }}
            />
          </div>

          <div className="form-buttons">
            <button type="submit" className="btn btn-primary">Save Expense</button>
            <button type="button" className="btn btn-secondary" onClick={onCancel}>Cancel</button>
            {initialValues && initialValues.id && (
              <button type="button" className="btn btn-danger" onClick={handleDelete}>Delete</button>
            )}
          </div>
        </form>
      </div>

      <style jsx>{`
        .modal-backdrop {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background-color: rgba(0, 0, 0, 0.5);
          display: flex;
          justify-content: center;
          align-items: center;
          z-index: 1050;
        }
        
        .form-container {
          background-color: white;
          border-radius: 8px;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
          width: 500px;
          max-width: 90%;
          max-height: 90vh;
          overflow-y: auto;
          padding: 24px;
        }
        
        .form-title {
          font-size: 18px;
          font-weight: 600;
          text-align: center;
          margin-bottom: 20px;
          color: #333;
        }
        
        .form-group {
          margin-bottom: 16px;
        }
        
        .form-label {
          display: block;
          margin-bottom: 8px;
          font-weight: 500;
          color: #555;
        }
        
        .form-control {
          width: 100%;
          padding: 10px;
          border: 1px solid #ddd;
          border-radius: 4px;
          font-size: 14px;
        }
        
        .currency-input-group {
          display: flex;
          align-items: center;
        }
        
        .currency-symbol {
          margin-right: 8px;
          font-size: 16px;
        }
        
        .form-select {
          padding: 10px;
          border: 1px solid #ddd;
          border-radius: 4px;
          font-size: 14px;
          width: 100%;
        }
        
        .notification-preferences {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }
        
        .form-check {
          display: flex;
          align-items: center;
          gap: 6px;
        }
        
        .form-check-input {
          margin: 0;
        }
        
        .emoji-picker-container {
          position: relative;
        }
        
        .emoji-button {
          background: none;
          border: 1px solid #ddd;
          border-radius: 4px;
          font-size: 24px;
          padding: 4px 12px;
          cursor: pointer;
        }
        
        .emoji-picker-wrapper {
          position: absolute;
          z-index: 1060;
        }
        
        .form-buttons {
          display: flex;
          justify-content: space-between;
          margin-top: 24px;
        }
        
        .btn {
          padding: 10px 16px;
          border-radius: 4px;
          font-weight: 500;
          cursor: pointer;
          border: none;
          font-size: 14px;
        }
        
        .btn-primary {
          background-color: #333;
          color: white;
          flex: 1;
          margin-right: 8px;
        }
        
        .btn-secondary {
          background-color: #ddd;
          color: #333;
          flex: 1;
          margin-left: 8px;
        }
        
        .btn-danger {
          background-color: #dc3545;
          color: white;
          margin-left: 8px;
        }
      `}</style>
    </div>
  );
};

export default ExpenseForm;